import React from 'react'
import algoliasearch from 'algoliasearch/lite'
import {
    Configure,
    connectAutoComplete,
    InstantSearch,
} from 'react-instantsearch-dom'
import generateMerchantUrl from '../../utils/merchantUrlGenerator'
import Box from '@material-ui/core/Box'
import Autocomplete from '@material-ui/lab/Autocomplete'
import TextField from '@material-ui/core/TextField'
import Link from '@material-ui/core/Link'
import makeStyles from '@material-ui/core/styles/makeStyles'
import InputAdornment from '@material-ui/core/InputAdornment'
import SearchIcon from '@material-ui/icons/Search'
import Divider from '@material-ui/core/Divider'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'

const useStyles = makeStyles((theme) => ({
    resultOption: {
        color: '#111',
        width: '100%',
    },
    resultOptionFlexContainer: {
        padding: '.5rem 1rem',
        '&:hover': {
            cursor: 'pointer',
            background: '#F5F5F5',
        },
    },
    resultOptionImageWrapper: {
        maxWidth: '60px',
        '& img': {
            display: 'block',
            borderRadius: '1px',
        },
    },
    resultOptionMerchantName: {},
    resultOptionSavingsText: {
        color: '#494949',
        fontSize: '0.9em',
        paddingLeft: '1em',
    },
    autoCompleteInputRoot: {
        padding: '0!important',
        backgroundColor: '#0A3A52',
        cursor: 'pointer',
        maxWidth: '36rem',
        margin: '0 auto',
        borderRadius: '21px',
        [theme.breakpoints.up('lg')]: {
            maxWidth: '62rem',
        },
        [theme.breakpoints.up('xl')]: {
            maxWidth: '86rem',
        },
    },
    autoCompleteInputElement: {
        backgroundColor: '#EBEBEB',
        borderRadius: '20px 0 0 20px',
        fontSize: '16px',
        padding: '12px 10px !important',
        '&::placeholder ': {
            color: '#6A6A6A',
        },
    },
    autoCompleteNotchedOutline: {
        border: 'none',
    },
    searchIcon: {
        color: 'white',
        fontSize: '1.9rem',
        margin: '0 10px 0 6px',
    },
    groupLabel: {
        fontSize: '10px',
        fontWeight: 550,
        letterSpacing: '.4px',
        color: '#262B32',
        fontFamily: 'Roboto, sans-serif',
        paddingTop: 0,
        paddingLeft: '24px',
        paddingRight: '24px',
    },
    groupList: {
        padding: '0!important',
        margin: 0,
        listStyle: 'none',
    },
    occasionsGroupList: {
        display: 'flex',
        flexDirection: 'column',
        listStyle: 'none',
        padding: 0,
        margin: 0,
        '& > li': {
            display: 'block', // override any global inline li styles
            width: '100%',
        },
        [theme.breakpoints.up(1536)]: {
            flexDirection: 'row', // switch to horizontal on desktop
            '& > li': {
                width: 'auto',
            },
        },
    },
    divider: {
        marginLeft: '24px',
        marginRight: '24px',
        borderColor: '#FCEDFF',
    },
    autocompleteOption: {
        minHeight: 'unset',
    },
    autocompleteListbox: {
        padding: 0,
        margin: 0,
    },
}))

const ResultOption = (merchant, classes, baseUrl) => {
    const merchantUrl = generateMerchantUrl(baseUrl, merchant, false, true)
    const isOpenLoop = ['visa', 'mastercard'].includes(merchant.slug)
    const maxDiscountPercentage = parseFloat(merchant.max_discount_percentage)
    const maxCashBackValue = parseFloat(merchant.max_cash_back_value)

    return (
        <Link
            href={merchantUrl}
            className={classes.resultOption}
            underline={'none'}
            key={merchantUrl}
        >
            <Box
                display="flex"
                alignItems="center"
                width={'100%'}
                lineHeight={1}
                className={classes.resultOptionFlexContainer}
            >
                <Box className={classes.resultOptionImageWrapper}>
                    <img
                        src={merchant.merchantImageUrl}
                        alt={`${merchant.name} Gift Card`}
                        width={'100%'}
                    />
                </Box>
                <Box width={'100%'} paddingLeft={'1em'} fontSize={'0.9em'}>
                    <span className={classes.resultOptionMerchantName}>
                        {merchant.name}
                    </span>
                    <span className={classes.resultOptionSavingsText}>
                        {!isOpenLoop && maxCashBackValue > 0 && (
                            <span>
                                up to ${maxCashBackValue.toFixed(2)} cash back
                            </span>
                        )}
                        {!isOpenLoop && maxDiscountPercentage > 0 && (
                            <span>
                                {' '}
                                up to {Math.round(maxDiscountPercentage)}% off
                            </span>
                        )}
                    </span>
                </Box>
            </Box>
        </Link>
    )
}

const navToRefinedBuyPage = (query) => {
    window.location.href = '/buy-gift-cards/?query=' + query
}

const ClickableSearchIcon = ({ currentRefinement }) => {
    const classes = useStyles()
    return (
        <InputAdornment
            position={'end'}
            onClick={() => navToRefinedBuyPage(currentRefinement)}
        >
            <SearchIcon className={classes.searchIcon} />
        </InputAdornment>
    )
}

const MaterialUISearchBox = ({
    hits,
    currentRefinement,
    refine,
    baseUrl,
    setIsSearchDropdownOpen,
    headerMenuData,
    isSmallerScreen,
}) => {
    const classes = useStyles()
    const lastOccasion =
        headerMenuData?.searchDropDown?.[3]?.sendContent?.[3]?.linkText
    const secondPopular =
        headerMenuData?.searchDropDown?.[0]?.sendContent?.[1]?.linkText
    const isSecondDiscount =
        headerMenuData?.searchDropDown[2]?.sendContent[1]?.linkText
    const isFeaturedBrand =
        headerMenuData?.searchDropDown?.[1]?.sendContent?.[0]?.linkText
    const isSecondOccasion =
        headerMenuData?.searchDropDown?.[3]?.sendContent?.[1]?.linkText
    const isThirdOccasion =
        headerMenuData?.searchDropDown?.[3]?.sendContent?.[2]?.linkText
    const isFourthOccasion =
        headerMenuData?.searchDropDown?.[3]?.sendContent?.[3]?.linkText
    const isFirstPopular =
        headerMenuData?.searchDropDown?.[0]?.sendContent?.[0]?.linkText
    const isFirstDiscount =
        headerMenuData?.searchDropDown?.[2]?.sendContent?.[0]?.linkText
    const isFirstOccasion =
        headerMenuData?.searchDropDown?.[3]?.sendContent?.[0]?.linkText

    const defaultCategoryOptions = [
        {
            optionType: 'popular',
            name: headerMenuData?.searchDropDown?.[0]?.sendContent?.[0]
                ?.linkText,
            image: headerMenuData?.searchDropDown?.[0]?.sendContent?.[0]
                ?.imageUrl,
            url: headerMenuData?.searchDropDown?.[0]?.sendContent?.[0]?.href,
        },
        {
            optionType: 'popular',
            name: headerMenuData?.searchDropDown?.[0]?.sendContent?.[1]
                ?.linkText,
            image: headerMenuData?.searchDropDown?.[0]?.sendContent?.[1]
                ?.imageUrl,
            url: headerMenuData?.searchDropDown?.[0]?.sendContent?.[1]?.href,
        },
        {
            optionType: 'featured',
            name: headerMenuData?.searchDropDown?.[1]?.sendContent?.[0]
                ?.linkText,
            image: headerMenuData?.searchDropDown?.[1]?.sendContent?.[0]
                ?.imageUrl,
            url: headerMenuData?.searchDropDown?.[1]?.sendContent?.[0]?.href,
        },
        {
            optionType: 'discount',
            name: headerMenuData?.searchDropDown?.[2]?.sendContent?.[0]
                ?.linkText,
            image: headerMenuData?.searchDropDown?.[2]?.sendContent?.[0]
                ?.imageUrl,
            url: headerMenuData?.searchDropDown?.[2]?.sendContent?.[0]?.href,
        },
        {
            optionType: 'discount',
            name: headerMenuData?.searchDropDown?.[2]?.sendContent?.[1]
                ?.linkText,
            image: headerMenuData?.searchDropDown?.[2]?.sendContent?.[1]
                ?.imageUrl,
            url: headerMenuData?.searchDropDown?.[2]?.sendContent?.[1]?.href,
        },
        {
            optionType: 'occasion',
            name: headerMenuData?.searchDropDown?.[3]?.sendContent?.[0]
                ?.linkText,
            image: headerMenuData?.searchDropDown?.[3]?.sendContent?.[0]
                ?.imageUrl,
            url: headerMenuData?.searchDropDown?.[3]?.sendContent?.[0]?.href,
        },
        {
            optionType: 'occasion',
            name: headerMenuData?.searchDropDown?.[3]?.sendContent?.[1]
                ?.linkText,
            image: headerMenuData?.searchDropDown?.[3]?.sendContent?.[1]
                ?.imageUrl,
            url: headerMenuData?.searchDropDown?.[3]?.sendContent?.[1]?.href,
        },
        {
            optionType: 'occasion',
            name: headerMenuData?.searchDropDown?.[3]?.sendContent?.[2]
                ?.linkText,
            image: headerMenuData?.searchDropDown?.[3]?.sendContent?.[2]
                ?.imageUrl,
            url: headerMenuData?.searchDropDown?.[3]?.sendContent?.[2]?.href,
        },
        {
            optionType: 'occasion',
            name: headerMenuData?.searchDropDown?.[3]?.sendContent?.[3]
                ?.linkText,
            image: headerMenuData?.searchDropDown?.[3]?.sendContent?.[3]
                ?.imageUrl,
            url: headerMenuData?.searchDropDown?.[3]?.sendContent?.[3]?.href,
        },
    ].filter((o) => o.name && o.url)

    const defaultOrCustomDropdown = currentRefinement
        ? hits
        : defaultCategoryOptions

    const getCategoryGroupLabel = (option) => {
        switch (option.optionType) {
            case 'popular':
                return headerMenuData?.searchDropDown?.[0]?.name || 'Popular'
            case 'featured':
                return headerMenuData?.searchDropDown?.[1]?.name || 'Featured'
            case 'discount':
                return headerMenuData?.searchDropDown?.[2]?.name || 'Discount'
            case 'occasion':
                return headerMenuData?.searchDropDown?.[3]?.name || 'Occasions'
            default:
                return ''
        }
    }

    return (
        <Autocomplete
            id={'headerSearchAutocompleteInput'}
            freeSolo
            // Show all category options at once (remove internal list scroll)
            ListboxProps={{
                style: {
                    maxHeight: 'none',
                    overflow: 'visible',
                },
            }}
            classes={{
                option: classes.autocompleteOption,
                listbox: classes.autocompleteListbox,
            }}
            options={defaultOrCustomDropdown}
            groupBy={(option) => getCategoryGroupLabel(option)}
            renderGroup={(params) => (
                <li key={params.key}>
                    {params.group && (
                        <div
                            className={classes.groupLabel}
                            style={{
                                paddingTop:
                                    params.group === 'Most popular brands'
                                        ? '24px'
                                        : undefined,
                            }}
                        >
                            {params.group}
                        </div>
                    )}
                    <Divider className={classes.divider} />
                    {params.group !== 'Occasions and Categories' ? (
                        <ul className={classes.groupList}>{params.children}</ul>
                    ) : (
                        <ul className={classes.occasionsGroupList}>
                            {params.children}
                        </ul>
                    )}
                </li>
            )}
            getOptionLabel={(option) => option?.name || ''}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={'Search for discount gift cards'}
                    variant="outlined"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <ClickableSearchIcon
                                currentRefinement={currentRefinement}
                            />
                        ),
                        classes: {
                            root: classes.autoCompleteInputRoot,
                            input: classes.autoCompleteInputElement,
                            notchedOutline: classes.autoCompleteNotchedOutline,
                        },
                    }}
                />
            )}
            onInputChange={(event, value) => {
                refine(value)
            }}
            onFocus={() => setIsSearchDropdownOpen(true)}
            onBlur={() => setIsSearchDropdownOpen(false)}
            onChange={(event) => {
                if (event.key === 'Enter') {
                    event.preventDefault()
                    navToRefinedBuyPage(currentRefinement)
                }
            }}
            renderOption={(option) => {
                if (option?.optionType) {
                    const isOccasion = option.optionType === 'occasion'
                    const isDiscount = option.optionType === 'discount'
                    const imageWidth = isOccasion ? 14 : 60
                    const imageHeight = isOccasion ? 16 : 40
                    const isZeroTopPaddingMobile =
                        option.name === secondPopular ||
                        option.name === isSecondDiscount

                    return (
                        <Box
                            key={option.name + option.optionType}
                            className={
                                isOccasion
                                    ? classes.occasionsContainer
                                    : undefined
                            }
                        >
                            <Link
                                href={option.url}
                                underline={'none'}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    padding: '10px 8px 2px 8px',
                                    ...(option.name === lastOccasion && {
                                        paddingBottom: '24px',
                                    }),
                                    marginTop: isZeroTopPaddingMobile
                                        ? '-6px'
                                        : 0,
                                    paddingTop: '2px',
                                    ...(option.name === isFeaturedBrand && {
                                        paddingBottom: '10px',
                                        paddingTop: '10px',
                                    }),
                                    ...(option.name === isFirstOccasion && {
                                        paddingTop: '10px',
                                        paddingBottom: '2px',
                                    }),
                                    ...(option.name === isSecondOccasion && {
                                        paddingTop: 0,
                                        marginTop: isSmallerScreen
                                            ? '-8px'
                                            : '10px',
                                    }),
                                    ...(option.name === isThirdOccasion && {
                                        paddingTop: 0,
                                        marginTop: isSmallerScreen
                                            ? '-8px'
                                            : `10px`,
                                    }),
                                    ...(option.name === isFourthOccasion && {
                                        paddingTop: 0,
                                        marginTop: isSmallerScreen
                                            ? '-8px'
                                            : `10px`,
                                    }),
                                    ...(option.name === secondPopular && {
                                        paddingBottom: '10px',
                                        paddingTop: '0px',
                                    }),
                                    ...(option.name === isSecondDiscount && {
                                        paddingBottom: '10px',
                                    }),
                                    ...(option.name === isFirstPopular && {
                                        paddingTop: '10px',
                                    }),
                                    ...(option.name === isFirstDiscount && {
                                        paddingTop: '10px',
                                    }),
                                }}
                            >
                                {option.image && (
                                    <Box
                                        className={
                                            classes.resultOptionImageWrapper
                                        }
                                    >
                                        <img
                                            src={option.image}
                                            alt={`${option.name} Gift Card`}
                                            width={imageWidth}
                                            height={imageHeight}
                                        />
                                    </Box>
                                )}
                                <Box
                                    paddingLeft={option.image ? '12px' : 0}
                                    fontSize={'12px'}
                                    display={'flex'}
                                    flexDirection={'column'}
                                    justifyContent={'center'}
                                    lineHeight={1.2}
                                >
                                    <Box
                                        style={{
                                            color: '#262B32',
                                            fontWeight: isOccasion ? 600 : 550,
                                            fontSize: isDiscount
                                                ? '12px'
                                                : '16px',
                                        }}
                                    >
                                        {option.name}
                                    </Box>
                                    {isDiscount && (
                                        <Box marginTop={'2px'}>
                                            <span
                                                style={{
                                                    fontSize: '12px',
                                                    color: '#393939',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                <span>up to 5% off</span>
                                            </span>
                                        </Box>
                                    )}
                                </Box>
                            </Link>
                        </Box>
                    )
                }

                return (
                    <li
                        key={option.objectID || option.slug}
                        style={{ listStyle: 'none', width: '100%' }}
                    >
                        {ResultOption(option, classes, baseUrl)}
                    </li>
                )
            }}
        />
    )
}

const CustomAutoComplete = connectAutoComplete(MaterialUISearchBox)

const HeaderSearchComponent = ({
    baseUrl,
    setIsSearchDropdownOpen,
    headerMenuData,
}) => {
    const theme = useTheme()
    const isSmallerScreen = useMediaQuery(theme.breakpoints.down(1536))

    const algoliaApplicationId = window.React.algolia_application_id
    const algoliaSearchApiKey = window.React.algolia_search_api_key
    const algoliaMerchantIndex = window.React.algolia_merchant_index

    const algoliaClient = algoliasearch(
        algoliaApplicationId,
        algoliaSearchApiKey
    )
    const searchClient = {
        search(requests) {
            if (requests.every(({ params }) => !params.query)) {
                return Promise.resolve({
                    results: requests.map(() => ({
                        hits: [],
                        nbHits: 0,
                        nbPages: 0,
                        page: 0,
                        processingTimeMS: 0,
                    })),
                })
            }
            return algoliaClient.search(requests)
        },
    }

    return (
        <InstantSearch
            searchClient={searchClient}
            indexName={algoliaMerchantIndex}
        >
            <CustomAutoComplete
                baseUrl={baseUrl}
                setIsSearchDropdownOpen={setIsSearchDropdownOpen}
                headerMenuData={headerMenuData}
                isSmallerScreen={isSmallerScreen}
            />
            <Configure
                hitsPerPage={4}
                filters={'local:false AND enabled:true'}
            />
        </InstantSearch>
    )
}

export default HeaderSearchComponent
